import React, { Fragment, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Provider } from "mobx-react";
import { HashRouter, useRoutes, useLocation, Outlet } from "react-router-dom";
import createTheme from "@mui/material/styles/createTheme";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { red, green, blue } from "@mui/material/colors";
import { SnackbarProvider } from "notistack";

import "./index.scss";
import stores from "./stores/stores";
import About from "./pages/about/about";
import JobHome from "./pages/job-home/jobHome";
import ServerPredictHome from "./pages/Submit/home/ServerPredictHome";
import AnticancerPrediction from "./pages/Submit/Anticancer/Anticancer";
import StructurePredictForm from "./pages/Submit/Structure/Structure";

import BindingPrediction from "./pages/Submit/Binding/Binding";
import DetectabilityPrediction from "./pages/Submit/Detectability/Detectability";

import TherapeuticPriction from "./pages/Submit/Therapeutic/Therapeutic";
import AffinityPrediction from "./pages/Submit/Affinity/Affinity ";

import Header from "./components/Header/Header";
import Footer from "./components/Footer/Footer";
import HomePage from "./pages/home/homePage";
import Reference from "./pages/reference/ reference";
import BackTop from "./components/ToTop/BackTop";

import ResultHome from "./pages/result/home/ResultHome";

import {
  AssignmentTurnedInRounded,
  BadgeRounded,
  FormatQuoteRounded,
  HomeRounded,
  Storage,
} from "@mui/icons-material";
import UpdateIcon from "@mui/icons-material/Update";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";

// import BigImage from "./components/BigImage/BigImage";
import Tutorial from "./pages/tutorial/tutorial";
import Update from "./pages/update/update";

const route = {
  path: "/",
  title: "Home",
  element: <Outlet />,
  children: [
    {
      title: "Home",
      icon: <HomeRounded />,
      index: true,
      element: <HomePage />,
    },
    {
      title: "Server",
      icon: <Storage />,
      path: "server",
      element: <Outlet />,
      children: [
        {
          title: "Peptide Detectability Prediction",
          path: "Detectability",
          element: (
            <Box ml={17} mr={17} mt={5}>
              <Card
                elevation={3}
                sx={{
                  mb: 1,
                }}
              >
                <CardContent
                  sx={{
                    borderBottom: "4px solid",
                    paddingBottom: "calc(10px - 0.35em)",
                    borderBottomColor: "transparent",
                  }}
                >
                  <Typography gutterBottom variant={"h6"} component="div">
                    Peptide Detectability Prediction
                  </Typography>
                </CardContent>
              </Card>
              {/* <Divider/> */}
              <Box sx={{ width: "100%", mb: 3 }}>
                <DetectabilityPrediction />
              </Box>
            </Box>
          ),
        },
        {
          title: "Protein Structure Prediction",
          path: "Structure",
          element: (
            <Box ml={17} mr={17} mt={5}>
              <Card
                elevation={3}
                sx={{
                  // height: "100%",
                  mb: 1,
                }}
              >
                <CardContent
                  sx={{
                    borderBottom: "4px solid",
                    paddingBottom: "calc(10px - 0.35em)",
                    borderBottomColor: "transparent",
                  }}
                >
                  <Typography gutterBottom variant={"h6"} component="div">
                    Protein Structure Prediction
                  </Typography>
                </CardContent>
              </Card>
              {/* <Divider/> */}
              <Box sx={{ width: "100%", mb: 3 }}>
                <StructurePredictForm />
              </Box>
            </Box>
          ),
        },
        {
          title: "Functional annotation",
          path: "Functional",
          element: <ServerPredictHome />,
          children: [
            {
              title: "Anti-cancer peptides",
              path: "Anticancer",
              element: <Outlet />,
              children: [{ index: true, element: <AnticancerPrediction /> }],
            },
            {
              title: "Therapeutic peptides",
              path: "Therapeutic",
              element: <Outlet />,
              children: [{ index: true, element: <TherapeuticPriction /> }],
            },
            {
              title: "Protein binding site",
              path: "Binding",
              element: <Outlet />,
              children: [{ index: true, element: <BindingPrediction /> }],
            },
            {
              title: "Protein affinity",
              path: "Affinity",
              element: <Outlet />,
              children: [{ index: true, element: <AffinityPrediction /> }],
            },
          ],
        },
      ],
    },
    {
      title: "Task List",
      icon: <AssignmentTurnedInRounded />,
      path: "tasks",
      element: <JobHome />,
    },
    // { title: "ResultOld", path: "result_old", element: <Result /> },
    {
      title: "Result",
      path: "result",
      element: <ResultHome />,
      maxWidth: "xl",
    },
    {
      title: "Tutorial",
      icon: <AutoStoriesIcon />,
      path: "tutorial",
      element: <Tutorial />,
    },

    {
      title: "What's new",
      icon: <UpdateIcon />,
      path: "update",
      element: <Update />,
    },
    {
      title: "Reference",
      icon: <FormatQuoteRounded />,
      path: "reference",
      element: <Reference />,
    },
    {
      title: "Contact us",
      icon: <BadgeRounded />,
      path: "about",
      element: <About />,
    },
  ],
};

function Routes() {
  const location = useLocation();

  return (
    <Fragment>
      {/* {location.pathname !== '/' && <WholeBreadcrumbs route={route} />} */}
      <Box flex={1}>{useRoutes([route])}</Box>
      {location.pathname !== "/" && <Footer />}
    </Fragment>
  );
}

function Index() {
  const theme = createTheme({
    palette: {
      primary: {
        main: "#0169a4",
        light: "#4e7cae",
        dark: "#1e4670",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#f6903d",
        light: "#ffc16c",
        dark: "#be6107",
        contrastText: "#ffffff",
      },
      lightError: {
        main: red[500],
        contrastText: "#ffffff",
      },
      lightSuccess: {
        main: green[500],
        contrastText: "#ffffff",
      },
      gray: {
        main: "#777777",
        contrastText: "#ffffff",
      },
    },
    components: {
      MuiTableCell: {
        variants: [
          {
            props: {
              variant: "head",
            },
            style: {
              fontWeight: "bold",
            },
          },
        ],
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1920,
      },
    },
  });

  const pageRef = useRef(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [firstInsert, setFirstInsert] = useState(false);
  


  return (
    <ThemeProvider theme={theme}>
      <Provider store={stores}>
        <SnackbarProvider autoHideDuration={2000}>
          <HashRouter>
            <Header route={route} />
            <div className="main" ref={pageRef}>
              <div id="back-to-top-anchor"></div>
              <BackTop target={pageRef} />
              <Routes />
              <Dialog
                open={isModalVisible}
                onClose={() => {
                  setIsModalVisible(false);
                }}
              >
                <DialogTitle> Notice</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                  DeepPEP will be shut down from 9 P.M. Feb. 22 to 8 A.M. Feb. 24 (UTC/GMT +8.00) for machine maintenance. We hope it won't inconvenience you too much
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => {
                      setIsModalVisible(false);
                    }}
                  >
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
            </div>
          </HashRouter>
        </SnackbarProvider>
      </Provider>
    </ThemeProvider>
  );
}

ReactDOM.render(<Index />, document.getElementById("root"));
